/**
 * Urlopy i absencje — widok HR (spec 01 §7).
 *
 * Trzy sekcje z makiety: kalendarz zespołu na tydzień, wnioski do rozpatrzenia
 * i salda. Wszystko z realnych wniosków — pusty tydzień mówi wprost, że nikogo
 * nie ma na urlopie, zamiast pokazywać chipy z makiety [D19].
 *
 * Kalendarz pokazuje też zwolnienia z rejestru i święta (Fala 7, grupa 149):
 * zwolnienie przychodzi z API jako sam fakt nieobecności — kadry widzą
 * „Zwolnienie lekarskie", przełożony „Nieobecność" i wyłącznie swój zespół
 * (ADR-0012 §7). Przyczyny i rodzaju świadczenia kalendarz nie dostaje wcale.
 *
 * Czwarta sekcja to REJESTR ZWOLNIEŃ LEKARSKICH (Etap 11): zwolnienie nie jest
 * wnioskiem, tylko faktem z e-ZLA, a chorobowe wchodzi na listę płac wyłącznie
 * stąd. Wpis z miesiąca z domkniętą listą płac jest nietykalny — poprawia się
 * go korektą listy, nie edycją rejestru (ADR-0012).
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Avatar,
  Badge,
  Button,
  Calendar,
  Card,
  Checkbox,
  ChevronLeft,
  ChevronRight,
  EmptyState,
  FormField,
  InfoBanner,
  Input,
  Modal,
  Select,
  Spinner,
  StatusPill,
  cx,
  useToast,
} from '@k1p4/ui'
import { formatDate, formatPLN } from '@k1p4/i18n'
import {
  ABSENCE_BENEFIT_TYPES,
  CARE_SCOPES,
  MATERNITY_LEAVE_KINDS,
  SICK_LEAVE_REASONS,
  mondayOf,
  shiftDay,
  warsawDay,
  type AbsenceBenefitType,
  type CareScope,
  type MaternityLeaveKind,
  type SickLeaveReason,
} from '@k1p4/shared'
import { ApiError, api, apiToday, type PendingLeaveItem, type TeamCalendarItem, apiYear } from '../../api/client'
import { useHasPermission } from '../../auth/AuthProvider'
import { formatCount } from '../portal/units'
import styles from './Leaves.module.css'

const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const

/** Dzisiejsza doba w Polsce — po północy w Warszawie w UTC trwa jeszcze wczoraj. */
function warsawToday(): string {
  return warsawDay(apiToday())
}

/**
 * Kolor chipa wg legendy z makiety (spec 01 §7): „Urlop" to urlop
 * wypoczynkowy, „Inne" — pozostałe urlopy i nieobecności z rejestru.
 */
function isLegendLeave(item: TeamCalendarItem): boolean {
  return item.kind === 'leave' && item.typeKey === 'wypoczynkowy'
}

interface SickForm {
  employeeId: string
  dateFrom: string
  dateTo: string
  reason: SickLeaveReason
  ezlaNumber: string
  note: string
  /** Rodzaj świadczenia (Etap 21) — od niego zależy pula i resztę pól. */
  benefitType: AbsenceBenefitType
  careScope: CareScope
  maternityKind: MaternityLeaveKind
  maternityCombined: boolean
}

/** „2026-03" → „03/2026" — miesiąc tak, jak go czyta księgowa. */
function monthLabel(month: string): string {
  return month.split('-').toReversed().join('/')
}

const EMPTY_SICK_FORM: SickForm = {
  employeeId: '',
  dateFrom: '',
  dateTo: '',
  reason: 'choroba',
  ezlaNumber: '',
  note: '',
  benefitType: 'chorobowe',
  careScope: 'dziecko_do_14',
  maternityKind: 'macierzynski',
  maternityCombined: false,
}

/** Odmowa serwera ma nazwany powód — pokazujemy go, a nie „coś poszło nie tak". */
function sickErrorKey(cause: unknown): string {
  if (cause instanceof ApiError) {
    /* Zapis przechodzi zawsze; odmowa dotyczy USUNIĘCIA z domkniętego miesiąca. */
    if (cause.code === 'PAYROLL_LOCKED') return 'hrLeaves.sick.errorLocked'
    if (cause.code === 'OVERLAPPING_LEAVE') return 'hrLeaves.sick.errorOverlap'
    if (cause.code === 'VALIDATION_ERROR') return 'hrLeaves.sick.errorRange'
  }
  return 'hrLeaves.errorGeneric'
}

/** Odmowa decyzji ma nazwany powód — wniosek po ustaniu zatrudnienia to nie „błąd zapisu". */
function decisionErrorKey(cause: unknown): string {
  if (cause instanceof ApiError && cause.code === 'AFTER_EMPLOYMENT_END') {
    return 'hrLeaves.errorAfterEmploymentEnd'
  }
  /* Urlop na dni z pracą w ewidencji — najpierw korekta zakresu albo wpisów (fala 7b, grupa 133). */
  if (cause instanceof ApiError && cause.code === 'WORK_ENTRIES_IN_RANGE') {
    return 'hrLeaves.errorWorkEntries'
  }
  return 'hrLeaves.errorGeneric'
}

/** Dni kolizji z odpowiedzi serwera, w formacie ekranu. */
function conflictDays(cause: unknown): string {
  const days = cause instanceof ApiError ? cause.details.days : undefined
  return Array.isArray(days) ? days.map((day) => formatDate(String(day), 'dot')).join(', ') : ''
}

export function Leaves() {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const commentId = useId()

  const [weekStart, setWeekStart] = useState(() =>
    mondayOf(warsawToday()),
  )
  const [decision, setDecision] = useState<{
    item: PendingLeaveItem
    action: 'approve' | 'reject' | 'request-change'
  } | null>(null)
  const [comment, setComment] = useState('')
  const [error, setError] = useState<string | null>(null)
  /* Dni z odpowiedzi odmowy — wstawiane w zdanie błędu (fala 7b, grupa 133). */
  const [errorDays, setErrorDays] = useState('')
  /* Zwolnienie na dni z pracą: kadry mogą zapisać je świadomie, z audytem. */
  const [sickWorkDays, setSickWorkDays] = useState<{ days: string; form: SickForm } | null>(null)

  const calendar = useQuery({
    queryKey: ['leaves', 'calendar', weekStart],
    queryFn: () => api.teamCalendar(weekStart),
  })
  const pending = useQuery({ queryKey: ['leaves', 'pending'], queryFn: () => api.pendingLeaves() })
  const balances = useQuery({ queryKey: ['leaves', 'balances'], queryFn: () => api.teamBalances() })
  /*
   * Rejestr zwolnień to dane o zdrowiu: czyta go dział HR i płace, a przełożony
   * — który wchodzi tu po wnioski urlopowe zespołu — nie. Sekcji nie renderujemy
   * i nawet o nią nie pytamy, zamiast pokazywać błąd 403 [D28].
   */
  const canViewLeaves = useHasPermission('Urlopy : Podgląd')
  const canViewPayroll = useHasPermission('Płace : Podgląd')
  const canReadSick = canViewLeaves || canViewPayroll
  const sickYear = apiYear()
  const sick = useQuery({
    queryKey: ['sick-leaves', sickYear],
    queryFn: () => api.sickLeaves(sickYear),
    enabled: canReadSick,
  })
  const staff = useQuery({
    queryKey: ['employees', 'sick-select'],
    queryFn: () => api.employees(),
    enabled: canReadSick,
  })

  const [sickForm, setSickForm] = useState<SickForm>(EMPTY_SICK_FORM)
  /*
   * Potwierdzenie dotyczy TEGO formularza — każda zmiana tworzy nowy obiekt
   * stanu, więc ostrzeżenie o innym zakresie po prostu przestaje obowiązywać.
   */
  const sickConfirm = sickWorkDays !== null && sickWorkDays.form === sickForm ? sickWorkDays.days : null
  const registerSick = useMutation({
    mutationFn: (acknowledgeWorkEntries: boolean) =>
      api.registerSickLeave({
        ...(acknowledgeWorkEntries ? { acknowledgeWorkEntries: true as const } : {}),
        employeeId: sickForm.employeeId,
        dateFrom: sickForm.dateFrom,
        dateTo: sickForm.dateTo,
        reason: sickForm.reason,
        ezlaNumber: sickForm.ezlaNumber.trim() || undefined,
        note: sickForm.note.trim() || undefined,
        benefitType: sickForm.benefitType,
        /* Parametry wysyłamy tylko z rodzajem, który ich wymaga. */
        careScope: sickForm.benefitType === 'opiekuncze' ? sickForm.careScope : undefined,
        maternityKind:
          sickForm.benefitType === 'macierzynskie' ? sickForm.maternityKind : undefined,
        maternityCombined:
          sickForm.benefitType === 'macierzynskie' ? sickForm.maternityCombined : undefined,
      }),
    onSuccess: (result) => {
      /*
       * Spóźnione e-ZLA zapisujemy zawsze, ale nie udajemy, że domknięta lista
       * płac je już uwzględnia — toast mówi, że miesiąc wymaga korekty (Etap 13).
       */
      if (result.correctionMonths.length > 0) {
        toast.show(
          t('hrLeaves.sick.registeredLate', {
            count: result.days,
            months: result.correctionMonths.map(monthLabel).join(', '),
          }),
          { tone: 'info' },
        )
      } else {
        toast.show(t('hrLeaves.sick.registered', { count: result.days }), { tone: 'success' })
      }
      setSickForm(EMPTY_SICK_FORM)
      setError(null)
      setSickWorkDays(null)
      void queryClient.invalidateQueries({ queryKey: ['sick-leaves'] })
    },
    onError: (cause: unknown) => {
      if (cause instanceof ApiError && cause.code === 'WORK_ENTRIES_IN_RANGE') {
        setSickWorkDays({ days: conflictDays(cause), form: sickForm })
        setError(null)
        return
      }
      setError(sickErrorKey(cause))
    },
  })
  const removeSick = useMutation({
    mutationFn: (id: string) => api.removeSickLeave(id),
    onSuccess: () => {
      toast.show(t('hrLeaves.sick.removed'), { tone: 'info' })
      setError(null)
      void queryClient.invalidateQueries({ queryKey: ['sick-leaves'] })
    },
    onError: (cause: unknown) => setError(sickErrorKey(cause)),
  })

  const decide = useMutation({
    mutationFn: () =>
      api.decideLeave(decision!.item.id, decision!.action, comment.trim() || undefined),
    onSuccess: () => {
      toast.show(
        t(
          decision!.action === 'approve'
            ? 'hrLeaves.approved'
            : decision!.action === 'reject'
              ? 'hrLeaves.rejected'
              : 'hrLeaves.changeRequested',
        ),
        { tone: decision!.action === 'approve' ? 'success' : 'info' },
      )
      setDecision(null)
      setComment('')
      void queryClient.invalidateQueries({ queryKey: ['leaves'] })
    },
    onError: (cause: unknown) => {
      setDecision(null)
      setErrorDays(conflictDays(cause))
      setError(decisionErrorKey(cause))
    },
  })

  if (calendar.isPending || pending.isPending || balances.isPending) {
    return <Spinner label={t('common.loading')} />
  }

  const days = WEEKDAYS.map((key, index) => ({ key, date: shiftDay(weekStart, index) }))
  const items = pending.data?.items ?? []
  const today = warsawToday()
  const holidays = new Set(calendar.data?.holidays ?? [])
  /* Nazwa pozycji dla czytnika i podpowiedzi — kolor chipa sam jej nie niesie. */
  const calendarLabel = (item: TeamCalendarItem): string =>
    item.kind === 'leave'
      ? item.typeLabel || t('hrLeaves.calendarKinds.leave')
      : t(`hrLeaves.calendarKinds.${item.kind}`)

  return (
    <>
      {error !== null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error, { days: errorDays })}
        </InfoBanner>
      ) : null}

      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <h3 className={styles.sectionTitle}>{t('hrLeaves.calendarTitle')}</h3>
          <span className={styles.weekNav}>
            <Button
              size="sm"
              variant="secondary"
              aria-label={t('hrLeaves.previousWeek')}
              onClick={() => setWeekStart(shiftDay(weekStart, -7))}
            >
              <ChevronLeft size={16} />
            </Button>
            <span className={styles.weekLabel}>
              {t('hrLeaves.calendarRange', {
                from: formatDate(weekStart, 'dot'),
                to: formatDate(shiftDay(weekStart, 6), 'dot'),
              })}
            </span>
            <Button
              size="sm"
              variant="secondary"
              aria-label={t('hrLeaves.nextWeek')}
              onClick={() => setWeekStart(shiftDay(weekStart, 7))}
            >
              <ChevronRight size={16} />
            </Button>
          </span>
        </div>

        <Card pad="md">
          <ul className={styles.legend} aria-label={t('hrLeaves.calendarLegend')}>
            <li className={styles.legendItem}>
              <span
                className={cx(styles.legendSwatch, styles.legendSwatchLeave)}
                aria-hidden="true"
              />
              {t('hrLeaves.calendarLegendLeave')}
            </li>
            <li className={styles.legendItem}>
              <span
                className={cx(styles.legendSwatch, styles.legendSwatchOther)}
                aria-hidden="true"
              />
              {t('hrLeaves.calendarLegendOther')}
            </li>
          </ul>
          <div className={styles.calendar}>
            {days.map((day) => {
              const onLeave = (calendar.data?.items ?? []).filter(
                (item) => item.dateFrom <= day.date && item.dateTo >= day.date,
              )
              const holiday = holidays.has(day.date)
              return (
                <div
                  key={day.key}
                  className={cx(
                    styles.day,
                    (day.key === 'sat' || day.key === 'sun' || holiday) && styles.dayOff,
                    day.date === today && styles.dayToday,
                  )}
                >
                  <span className={styles.dayLabel}>
                    {t(`hrTime.weekdays.${day.key}`)} {day.date.slice(8)}
                  </span>
                  {holiday ? (
                    <span className={styles.holiday}>{t('hrLeaves.calendarHoliday')}</span>
                  ) : null}
                  {onLeave.map((item) => (
                    <span
                      key={`${item.kind}-${item.id}`}
                      title={calendarLabel(item)}
                      className={cx(
                        styles.chip,
                        isLegendLeave(item) ? styles.chipLeave : styles.chipOther,
                      )}
                    >
                      {item.firstName.charAt(0)}. {item.lastName}
                      <span className={styles.visuallyHidden}>, {calendarLabel(item)}</span>
                    </span>
                  ))}
                </div>
              )
            })}
          </div>
          {(calendar.data?.items ?? []).length === 0 ? (
            <p className={styles.calendarEmpty}>{t('hrLeaves.calendarEmpty')}</p>
          ) : null}
        </Card>
      </section>

      <div className={styles.layout}>
        <section>
          <div className={styles.sectionHead}>
            <h3 className={styles.sectionTitle}>{t('hrLeaves.pendingTitle')}</h3>
            {items.length > 0 ? (
              <Badge tone="soft">{t('hrLeaves.pendingBadge', { count: items.length })}</Badge>
            ) : null}
          </div>

          {items.length === 0 ? (
            <EmptyState
              icon={<Calendar size={28} />}
              title={t('hrLeaves.pendingEmpty')}
              description={t('hrLeaves.pendingEmptyBody')}
            />
          ) : (
            <div className={styles.requests}>
              {items.map((item) => (
                <Card key={item.id} pad="md" className={styles.request}>
                  <Avatar name={`${item.firstName} ${item.lastName}`} size={40} />
                  <div className={styles.requestBody}>
                    <p className={styles.requestName}>
                      {item.firstName} {item.lastName}
                    </p>
                    <p className={styles.requestMeta}>
                      {item.typeLabel} ·{' '}
                      {t('hrLeaves.range', {
                        from: formatDate(item.dateFrom, 'dot'),
                        to: formatDate(item.dateTo, 'dot'),
                      })}{' '}
                      · {item.days}
                    </p>
                    {item.note ? <p className={styles.requestNote}>{item.note}</p> : null}
                    {/*
                      Wniosek sięga poza ostatni dzień zatrudnienia (grupa 44):
                      urlopu po ustaniu się nie udziela (art. 171 § 1 k.p.),
                      więc akceptacji nie ma — odrzucić i poprosić o zmianę można.
                    */}
                    {item.afterEmploymentEnd ? (
                      <p className={styles.requestNote} id={`after-end-${item.id}`}>
                        <Badge tone="warning">{t('hrLeaves.afterEmploymentEnd')}</Badge>{' '}
                        {item.employmentEndAt
                          ? t('hrLeaves.afterEmploymentEndHint', {
                              date: formatDate(item.employmentEndAt, 'dot'),
                            })
                          : t('hrLeaves.afterEmploymentEndHintNoDate')}
                      </p>
                    ) : null}
                  </div>
                  <div className={styles.requestActions}>
                    <Button
                      size="sm"
                      variant="success"
                      disabled={item.afterEmploymentEnd === true}
                      aria-describedby={item.afterEmploymentEnd ? `after-end-${item.id}` : undefined}
                      onClick={() => setDecision({ item, action: 'approve' })}
                    >
                      {t('hrLeaves.approve')}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      className={styles.reject}
                      onClick={() => setDecision({ item, action: 'reject' })}
                    >
                      {t('hrLeaves.reject')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setDecision({ item, action: 'request-change' })}
                    >
                      {t('hrLeaves.requestChange')}
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>

        <aside>
          <h3 className={styles.sectionTitle}>{t('hrLeaves.balancesTitle')}</h3>
          <Card pad="md" className={styles.balances}>
            {(balances.data?.items ?? []).length === 0 ? (
              <p className={styles.calendarEmpty}>{t('hrLeaves.balancesEmpty')}</p>
            ) : (
              (balances.data?.items ?? []).map((row) => {
                /*
                 * Limit KAŻDEJ osoby z jej etatu i rodzaju umowy (Fala 7, grupa 24),
                 * a pasek z godzin puli — przy 1/2 etatu dzień urlopu zużywa 4 h,
                 * więc dni wniosków podzielone przez limit w dniach dawały 200 %.
                 */
                if (row.notApplicable) {
                  return (
                    <div key={row.employeeId} className={styles.balance}>
                      <div className={styles.balanceHead}>
                        <span>
                          {row.firstName} {row.lastName}
                        </span>
                        <span className={styles.balanceValue}>—</span>
                      </div>
                      <span className={styles.balanceNote}>{t('hrLeaves.balanceNotApplicable')}</span>
                    </div>
                  )
                }
                const limit = row.limitDays ?? 0
                const limitHours = row.limitHours ?? null
                const usedHours = row.usedHours ?? null
                const percent =
                  limitHours !== null && usedHours !== null
                    ? limitHours > 0
                      ? Math.min(100, Math.round((usedHours / limitHours) * 100))
                      : 0
                    : limit > 0
                      ? Math.min(100, Math.round((row.usedDays / limit) * 100))
                      : 0
                return (
                  <div key={row.employeeId} className={styles.balance}>
                    <div className={styles.balanceHead}>
                      <span>
                        {row.firstName} {row.lastName}
                      </span>
                      <span className={styles.balanceValue}>
                        {row.remainingDays === null ? '—' : formatCount(i18n.language, row.remainingDays)}
                      </span>
                    </div>
                    <div className={styles.balanceTrack}>
                      <span className={styles.balanceFill} style={{ width: `${percent}%` }} />
                    </div>
                    <span className={styles.balanceNote}>
                      {limitHours !== null && usedHours !== null
                        ? t('hrLeaves.balanceUsedHours', {
                            count: limit,
                            used: formatCount(i18n.language, row.usedDays),
                            usedHours,
                            limitHours,
                          })
                        : t('hrLeaves.balanceUsed', {
                            count: limit,
                            used: formatCount(i18n.language, row.usedDays),
                          })}
                    </span>
                    {row.employmentEndAt ? (
                      <span className={styles.balanceNote}>
                        {t('hrLeaves.balanceEnding', { date: formatDate(row.employmentEndAt, 'dot') })}
                      </span>
                    ) : null}
                  </div>
                )
              })
            )}
            {(balances.data?.items ?? []).some((row) => row.baseAssumed) ? (
              <p className={styles.balanceNote}>{t('hrLeaves.balancesBaseAssumed')}</p>
            ) : null}
          </Card>
        </aside>
      </div>

      {canReadSick ? (
      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <h3 className={styles.sectionTitle}>{t('hrLeaves.sick.title')}</h3>
          <Badge tone="soft">{t('hrLeaves.sick.year', { year: sickYear })}</Badge>
        </div>

        {sick.data?.benefitPayer === false ? (
          <InfoBanner tone="info" className={styles.banner}>
            {t('hrLeaves.sick.zusPaysBenefits')}
          </InfoBanner>
        ) : null}

        {sick.data?.canRegister ? (
          <Card pad="md" className={styles.sickForm}>
            <FormField label={t('hrLeaves.sick.employee')} htmlFor="sick-employee">
              <Select
                id="sick-employee"
                value={sickForm.employeeId}
                onChange={(event) =>
                  setSickForm({ ...sickForm, employeeId: event.target.value })
                }
              >
                <option value="">{t('hrLeaves.sick.employeePlaceholder')}</option>
                {(staff.data?.items ?? [])
                  .filter((person) => person.status === 'aktywny')
                  .map((person) => (
                    <option key={person.id} value={person.id}>
                      {person.lastName} {person.firstName}
                    </option>
                  ))}
              </Select>
            </FormField>
            <FormField label={t('hrLeaves.sick.dateFrom')} htmlFor="sick-from">
              <Input
                id="sick-from"
                type="date"
                value={sickForm.dateFrom}
                onChange={(event) => setSickForm({ ...sickForm, dateFrom: event.target.value })}
              />
            </FormField>
            <FormField label={t('hrLeaves.sick.dateTo')} htmlFor="sick-to">
              <Input
                id="sick-to"
                type="date"
                value={sickForm.dateTo}
                onChange={(event) => setSickForm({ ...sickForm, dateTo: event.target.value })}
              />
            </FormField>
            {/*
              Rodzaj świadczenia decyduje o puli (Etap 21): chorobowe zużywa
              okres zasiłkowy, opiekuńcze limit roczny, macierzyńskie żadnej.
              Dlatego reszta pól zmienia się razem z nim. Etykiety są długie,
              więc pole zajmuje dwie kolumny siatki, a select niesie pełną nazwę
              w podpowiedzi — dawniej tekst był ucięty w połowie słowa (grupa 149).
            */}
            <FormField
              label={t('hrLeaves.sick.benefitType')}
              htmlFor="sick-benefit-type"
              className={styles.sickFormWide}
            >
              <Select
                id="sick-benefit-type"
                title={t(`hrLeaves.sick.benefitTypes.${sickForm.benefitType}`)}
                value={sickForm.benefitType}
                onChange={(event) =>
                  setSickForm({
                    ...sickForm,
                    benefitType: event.target.value as AbsenceBenefitType,
                  })
                }
              >
                {ABSENCE_BENEFIT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(`hrLeaves.sick.benefitTypes.${type}`)}
                  </option>
                ))}
              </Select>
            </FormField>
            {sickForm.benefitType === 'chorobowe' ? (
              <FormField label={t('hrLeaves.sick.reason')} htmlFor="sick-reason">
                <Select
                  id="sick-reason"
                  value={sickForm.reason}
                  onChange={(event) =>
                    setSickForm({ ...sickForm, reason: event.target.value as SickLeaveReason })
                  }
                >
                  {SICK_LEAVE_REASONS.map((reason) => (
                    <option key={reason} value={reason}>
                      {t(`hrLeaves.sick.reasons.${reason}`)}
                    </option>
                  ))}
                </Select>
              </FormField>
            ) : null}
            {sickForm.benefitType === 'opiekuncze' ? (
              <FormField
                label={t('hrLeaves.sick.careScope')}
                htmlFor="sick-care-scope"
                hint={t('hrLeaves.sick.careScopeHint')}
              >
                <Select
                  id="sick-care-scope"
                  value={sickForm.careScope}
                  onChange={(event) =>
                    setSickForm({ ...sickForm, careScope: event.target.value as CareScope })
                  }
                >
                  {CARE_SCOPES.map((scope) => (
                    <option key={scope} value={scope}>
                      {t(`hrLeaves.sick.careScopes.${scope}`)}
                    </option>
                  ))}
                </Select>
              </FormField>
            ) : null}
            {sickForm.benefitType === 'macierzynskie' ? (
              <>
                <FormField label={t('hrLeaves.sick.maternityKind')} htmlFor="sick-maternity-kind">
                  <Select
                    id="sick-maternity-kind"
                    value={sickForm.maternityKind}
                    onChange={(event) =>
                      setSickForm({
                        ...sickForm,
                        maternityKind: event.target.value as MaternityLeaveKind,
                      })
                    }
                  >
                    {MATERNITY_LEAVE_KINDS.map((kind) => (
                      <option key={kind} value={kind}>
                        {t(`hrLeaves.sick.maternityKinds.${kind}`)}
                      </option>
                    ))}
                  </Select>
                </FormField>
                <FormField
                  label={t('hrLeaves.sick.maternityCombined')}
                  htmlFor="sick-maternity-combined"
                  hint={t('hrLeaves.sick.maternityCombinedHint')}
                >
                  <Checkbox
                    id="sick-maternity-combined"
                    checked={sickForm.maternityCombined}
                    onChange={(event) =>
                      setSickForm({ ...sickForm, maternityCombined: event.target.checked })
                    }
                  />
                </FormField>
              </>
            ) : null}
            <FormField label={t('hrLeaves.sick.ezla')} htmlFor="sick-ezla">
              <Input
                id="sick-ezla"
                value={sickForm.ezlaNumber}
                placeholder={t('hrLeaves.sick.ezlaPlaceholder')}
                onChange={(event) => setSickForm({ ...sickForm, ezlaNumber: event.target.value })}
              />
            </FormField>
            {sickConfirm !== null ? (
              <InfoBanner tone="warning" className={styles.banner}>
                {t('hrLeaves.sick.workEntriesWarning', { days: sickConfirm })}
              </InfoBanner>
            ) : null}
            <div className={styles.sickFormActions}>
              <Button
                disabled={
                  registerSick.isPending ||
                  sickForm.employeeId === '' ||
                  sickForm.dateFrom === '' ||
                  sickForm.dateTo === ''
                }
                onClick={() => registerSick.mutate(sickConfirm !== null)}
              >
                {t(sickConfirm !== null ? 'hrLeaves.sick.registerAnyway' : 'hrLeaves.sick.register')}
              </Button>
            </div>
          </Card>
        ) : null}

        {/*
          Stan puli świadczeń (Etap 21): ile dni okresu zasiłkowego zostało
          i ile dni opieki wykorzystano w roku. Bez tego prawo do zasiłku widać
          dopiero na liście płac — czyli za późno.
        */}
        {(sick.data?.pools ?? []).length > 0 ? (
          <Card pad="md" className={styles.sickPools}>
            <p className={styles.sickPoolsTitle}>{t('hrLeaves.sick.pools')}</p>
            <ul className={styles.sickPoolsList}>
              {(sick.data?.pools ?? []).map((pool) => (
                <li key={pool.employeeId}>
                  <span className={styles.sickPoolName}>{pool.employeeName}</span>
                  {': '}
                  {t('hrLeaves.sick.poolBenefitPeriod', {
                    used: pool.benefitPeriodUsed,
                    limit: pool.benefitPeriodLimit,
                  })}
                  {pool.benefitPeriodEndsOn !== null ? (
                    <span className={styles.sickPoolWarn}>
                      {' '}
                      {t('hrLeaves.sick.poolExhausted', {
                        date: formatDate(pool.benefitPeriodEndsOn, 'dot'),
                      })}
                    </span>
                  ) : null}
                  {pool.careUsed.dziecko_do_14 +
                    pool.careUsed.dziecko_niepelnosprawne +
                    pool.careUsed.inny_czlonek_rodziny >
                  0 ? (
                    <span>
                      {' · '}
                      {t('hrLeaves.sick.poolCare', {
                        count:
                          pool.careUsed.dziecko_do_14 +
                          pool.careUsed.dziecko_niepelnosprawne +
                          pool.careUsed.inny_czlonek_rodziny,
                      })}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </Card>
        ) : null}

        {(sick.data?.items ?? []).length === 0 ? (
          <EmptyState
            icon={<Calendar size={28} />}
            title={t('hrLeaves.sick.empty')}
            description={t('hrLeaves.sick.emptyBody')}
          />
        ) : (
          <Card pad="flush" className={styles.sickList}>
            {(sick.data?.items ?? []).map((item) => (
              <div key={item.id} className={styles.sickRow}>
                <div className={styles.sickRowBody}>
                  <p className={styles.requestName}>{item.employeeName}</p>
                  <p className={styles.requestMeta}>
                    {t('hrLeaves.range', {
                      from: formatDate(item.dateFrom, 'dot'),
                      to: formatDate(item.dateTo, 'dot'),
                    })}{' '}
                    · {t('hrLeaves.sick.days', { count: item.days })} ·{' '}
                    {item.benefitType === 'chorobowe'
                      ? t(`hrLeaves.sick.reasons.${item.reason}`)
                      : item.benefitType === 'opiekuncze'
                        ? t(`hrLeaves.sick.careScopes.${item.careScope ?? 'dziecko_do_14'}`)
                        : t(
                            `hrLeaves.sick.maternityKinds.${item.maternityKind ?? 'macierzynski'}`,
                          )}
                  </p>
                  {item.baseGr !== null ? (
                    <p className={styles.requestNote}>
                      {t('hrLeaves.sick.base', {
                        amount: formatPLN(item.baseGr),
                        source: t(`hrLeaves.sick.baseKinds.${item.baseKind ?? 'historia_12m'}`),
                      })}
                    </p>
                  ) : (
                    <p className={styles.requestNote}>{t('hrLeaves.sick.baseAfterPayroll')}</p>
                  )}
                  {item.ezlaNumber !== null ? (
                    <p className={styles.requestNote}>
                      {t('hrLeaves.sick.ezlaValue', { number: item.ezlaNumber })}
                    </p>
                  ) : null}
                  {item.correctionMonths.length > 0 ? (
                    <p className={styles.sickCorrection}>
                      {t('hrLeaves.sick.correctionNeeded', {
                        months: item.correctionMonths.map(monthLabel).join(', '),
                      })}
                    </p>
                  ) : null}
                </div>
                <div className={styles.requestActions}>
                  {item.locked ? (
                    <StatusPill tone="neutral">{t('hrLeaves.sick.locked')}</StatusPill>
                  ) : sick.data?.canRegister ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={removeSick.isPending}
                      onClick={() => removeSick.mutate(item.id)}
                    >
                      {t('hrLeaves.sick.remove')}
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </Card>
        )}
      </section>
      ) : null}

      <Modal
        open={decision !== null}
        onClose={() => setDecision(null)}
        title={t('hrLeaves.decisionTitle')}
        footer={
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setDecision(null)}>
              {t('hrLeaves.cancel')}
            </Button>
            <Button disabled={decide.isPending} onClick={() => decide.mutate()}>
              {t('hrLeaves.confirm')}
            </Button>
          </div>
        }
      >
        {decision ? (
          <p className={styles.modalSummary}>
            {decision.item.firstName} {decision.item.lastName} · {decision.item.typeLabel} ·{' '}
            {formatDate(decision.item.dateFrom, 'dot')} – {formatDate(decision.item.dateTo, 'dot')}
          </p>
        ) : null}
        <FormField label={t('hrLeaves.decisionComment')} htmlFor={commentId}>
          <textarea
            id={commentId}
            className={styles.textarea}
            rows={3}
            placeholder={t('hrLeaves.decisionPlaceholder')}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
        </FormField>
      </Modal>
    </>
  )
}
